// App Banners: the choices the page offers and the shapes the API answers with.

import type { AfterDue, StoredLevel, StoredLevelSource } from './bannerApi';

/** The page's tabs, as written in the address (?tab=). */
export type BannerTab = 'personalized' | 'task' | 'history' | 'test';

/** The four test fires of the Test pushes tab. */
export type TestPushType = 'random_attendance' | 'mood_check' | 'celebration' | 'missed_attendance';
export type CelebrationType = 'birthday' | 'anniversary' | 'festival';
export type MissedSubtype = 'no-attendance' | 'missing-signout';

/**
 * Who a personalized announcement (or task) goes to: named individuals, whole
 * reporting teams, everyone on the selected project's roster at send time, or whoever
 * fits a set of store and employee details at send time.
 */
export type RecipientMode = 'employees' | 'managers' | 'project' | 'details';

/**
 * Something the employee has to DO, shown as a banner until it's done. The id is
 * the API's `taskType` and the mobile app's route key, so a new entry here also
 * needs server-side completion (and, for a button, an app screen to open).
 */
export type TaskType = 'read_this' | 'consent' | 'fill_form' | 'reach_level' | 'resume_upload';

/** What a task's settings have picked so far: its default copy names them. */
export interface TaskPick {
  /** What Reach a level checks. */
  scoreName: string | null;
  /** 'YYYY-MM'; '' for something that is not monthly. */
  month: string;
  /** A text field: there is no target to reach, only something to fill in. */
  hasValue?: boolean;
  /** What Fill a form opens. */
  formName: string | null;
}

export interface TaskDef {
  value: TaskType;
  label: string;
  /** The small line on the task's card. */
  cardLine: string;
  /** A word beside its name: this one wants the next app. */
  tag?: string;
  description: string;
  /** The button on the app card, mirrored in the preview. Null: no button. */
  ctaLabel: string | null;
  /** Finishes "Done when …" — the server-side completion rule. Null: nothing marks it done. */
  doneWhen: string | null;
  /** Beside "Also send a push": who the send leaves out. */
  pushHint: string;
  /** Finishes "N not pushed — …" after a send: why the API spared them the push. */
  alreadyDone: string;
  /** Under the phone preview. */
  previewNote: string;
  /** Ends "How it works": which app versions show it. `bannerOnly`: the picked form is one. */
  appNote: (sendPush: boolean, bannerOnly?: boolean) => string;
  defaultCopy: (pick: TaskPick) => { title: string; body: string };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM' → 'Sep 2026', as the server writes it on the banner. '' if not a month. */
export const monthLabel = (month: string): string => {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(month);
  return m ? `${MONTH_NAMES[Number(m[2]) - 1]} ${m[1]}` : '';
};

/** Last month as 'YYYY-MM' — usually the newest month with its scores uploaded. */
export const previousMonth = (now = new Date()): string => {
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** What a consent record is, and is not. Exactly this, wherever it is said. */
export const CONSENT_RECORD_NOTE =
  "A consent record shows what the person's app sent: their answer, the wording and the time. It has not been reviewed as legal proof.";

/** Which sign-in named the person who answered, in words. It says only what it knows. */
export const signInWords = (proof: string | null | undefined): string => {
  if (proof === 'app-pass') return 'Signed in by OTP on this phone';
  if (proof === 'web-pass') return 'Signed in on the website';
  if (proof === 'session') return 'Signed in with a session on our server';
  if (proof === 'own-token') return 'Signed in with a token from our server';
  return 'Sign-in not checked';
};

/** Behind the (i) beside it. */
export const SIGN_IN_NOTE = 'It shows whose sign-in was on the phone, not who was holding it.';

// Default copy is 1Office's own resume prompt, so employees see the same ask in
// both apps. Editable per send. In the order the cards show (the approved mockup's).
export const TASK_TYPES: TaskDef[] = [
  {
    value: 'read_this',
    label: 'Read this',
    cardLine: 'Notice, until a date',
    description: 'Stays until they press the button, or its date. On an older app there is no button: a plain banner until its date, or for 90 days.',
    // The words are the sender's: useTaskButtons.
    ctaLabel: null,
    doneWhen: 'they press the button',
    pushHint: 'Goes to everyone it is sent to.',
    alreadyDone: '',
    previewNote: '',
    appNote: () => 'An older app shows it as a plain banner, with no button.',
    defaultCopy: () => ({ title: '', body: '' }),
  },
  {
    value: 'consent',
    label: 'Give consent',
    cardLine: 'Agree or decline',
    tag: 'New app',
    // The one short line the owner asked for, behind the (i).
    description: CONSENT_RECORD_NOTE,
    ctaLabel: null,
    doneWhen: 'they answer',
    pushHint: 'Goes to everyone it is sent to.',
    alreadyDone: '',
    previewNote: '',
    appNote: () => 'An older app has no buttons: a plain banner whose first line asks them to update.',
    defaultCopy: () => ({ title: '', body: '' }),
  },
  {
    value: 'fill_form',
    label: 'Fill a form',
    cardLine: 'Any form you made',
    description: "Stays on each person's home page until they submit the form, counted from this send.",
    ctaLabel: 'Open form',
    doneWhen: 'they submit it',
    pushHint: 'Goes to everyone it is sent to.',
    alreadyDone: 'already submitted',
    previewNote: '',
    appNote: (_sendPush, bannerOnly) =>
      bannerOnly
        ? 'The button opens the form. An older app has no button: a plain banner whose first line asks them to update.'
        : 'The button opens the form. An older app has no button: a plain banner, and the form opens from their Tasks list.',
    defaultCopy: ({ formName }) =>
      formName ? { title: `Fill in ${formName}`, body: 'Please fill it in and submit it.' } : { title: '', body: '' },
  },
  {
    value: 'reach_level',
    label: 'Reach a level',
    cardLine: 'Any score or detail',
    description: "Stays on each person's home page until their figure reaches their target.",
    ctaLabel: null,
    doneWhen: 'their figure reaches the target',
    pushHint: 'Skips people already at their target.',
    alreadyDone: 'already at target',
    previewNote: 'The first line is filled in for each person.',
    appNote: () =>
      'People already at target get no card and no push unless their figure drops below it. ' +
      'The app shows it as a plain banner, with no button.',
    // Short: the app shows 3 lines before "Read more", and each person's line
    // comes first.
    defaultCopy: ({ scoreName, month, hasValue }) => ({
      title: hasValue ? `Fill in ${scoreName}` : scoreName ? `Reach your ${scoreName} target` : 'Reach your target',
      body: hasValue
        ? 'Please get this filled in.'
        : monthLabel(month)
        ? `Reach your target for ${monthLabel(month)}.`
        : 'Reach your target.',
    }),
  },
  {
    value: 'resume_upload',
    label: 'Upload resume',
    cardLine: 'Goes to the Vault',
    description:
      'Employees upload their resume to Document Vault → Personal. The banner disappears on its own once a resume is on file — uploaded from ChannelPlex or the 1Office app.',
    ctaLabel: 'Upload resume',
    doneWhen: 'a resume is on file',
    pushHint: 'Skips people we already know have a resume.',
    alreadyDone: 'already have a resume',
    previewNote: '"Upload resume" opens the app\'s upload screen.',
    appNote: (sendPush) =>
      'Only app versions that have the upload screen show it; older versions skip it rather ' +
      `than show a card with no button${sendPush ? ', though the push still reaches them' : ''}.`,
    defaultCopy: () => ({
      title: 'Help us get to know your skills and experience better',
      body: 'We have colleagues with diverse experience, qualifications, and skills. Our current profiles may not capture everything you bring from your previous roles. Please take a few minutes to share your professional background by uploading your resume.',
    }),
  },
];

/**
 * The kinds, as they stand. Until the app build with the buttons is in both stores, Read this is
 * a plain banner on every phone: no button, nothing to mark it done, Seen and Not seen only.
 */
export const taskTypesFor = (released: boolean): TaskDef[] =>
  released
    ? TASK_TYPES
    : TASK_TYPES.map((t) =>
        t.value === 'read_this'
          ? {
              ...t,
              description: 'A plain banner until its date, or for 90 days. The tracker shows who has seen it.',
              doneWhen: null,
              appNote: () => 'Every app shows it as a plain banner.',
            }
          : t
      );

/** How long a count waits after the last change before asking again. */
export const COUNT_DEBOUNCE_MS = 400;

/** "Reach a score" is no card of its own now, but sends stored as one still list by it. */
export const taskLabel = (taskType: string | null | undefined): string =>
  taskType === 'score_target'
    ? 'Reach a score'
    : TASK_TYPES.find((t) => t.value === taskType)?.label ?? taskType ?? 'Task';

/** A task judged on a figure: its tracker has a figure and a target, and "done" reads "reached". */
export const isFigureTask = (taskType: string | null | undefined): boolean =>
  taskType === 'score_target' || taskType === 'reach_level';

const TRAINING_LABELS: Record<string, string> = {
  modules_completed: 'Modules completed',
  points: 'Training points',
  average_score: 'Average module score',
  module_score: 'Module score',
  module_completed: 'Module completed',
};

/** A stored Reach a level source, in words: "Modules completed", "Demo Quality · Sep 2026". */
export const levelSourceLabel = (source: StoredLevelSource): string => {
  const name =
    source.kind === 'score_total'
      ? 'Employee Score total'
      : source.kind === 'training'
      ? [TRAINING_LABELS[source.metric ?? ''] ?? 'Training', source.moduleTitle].filter(Boolean).join(': ')
      : source.kind === 'field'
      ? source.field?.label ?? 'A field'
      : source.metricName ?? 'A score';
  const month = monthLabel(source.month ?? '');
  return month ? `${name} · ${month}` : name;
};

/** What a send asked for, in words, under its type. Null on a task with nothing to name. */
export const askedFor = (send: {
  taskType?: string | null;
  scoreTarget?: { metricName: string; month: string };
  fillForm?: { formName: string };
  reachLevel?: StoredLevel;
}): string | null =>
  send.taskType === 'score_target' && send.scoreTarget
    ? `${send.scoreTarget.metricName}${monthLabel(send.scoreTarget.month) && ` · ${monthLabel(send.scoreTarget.month)}`}`
    : send.taskType === 'fill_form' && send.fillForm
    ? send.fillForm.formName
    : send.taskType === 'reach_level' && send.reachLevel
    ? levelSourceLabel(send.reachLevel.source)
    : null;

/** A text field: nothing to reach, only something to fill in. */
export const isFillIn = (level: StoredLevel | undefined): boolean => level?.target.mode === 'has_value';

const isScoreLevel = (level: StoredLevel | undefined): boolean => level?.source.kind.startsWith('score_') ?? false;

/** A tracker's figure column, headed by what it holds. */
export const figureHead = (send: { taskType: string | null; reachLevel?: StoredLevel }): string => {
  const source = send.reachLevel?.source;
  if (send.taskType === 'score_target' || isScoreLevel(send.reachLevel)) return 'Score';
  if (source?.kind === 'training') return TRAINING_LABELS[source.metric ?? ''] ?? 'Figure';
  if (source?.kind === 'field') return source.field?.label ?? 'Figure';
  return 'Figure';
};

/** That column's words for someone with nothing in it yet. */
export const noFigureWords = (send: { taskType: string | null; reachLevel?: StoredLevel }): string =>
  isFillIn(send.reachLevel)
    ? 'Not filled in'
    : send.taskType === 'score_target' || isScoreLevel(send.reachLevel)
    ? 'No score yet'
    : 'No figure yet';

/**
 * How far down a selected manager's reporting line to reach. The org runs
 * Trade Head → AM → iPro, so "their team" means very different sizes depending
 * on who you picked — this makes the caller say which.
 */
export type TeamScope = 'self' | 'direct' | 'all';

export const TEAM_SCOPES: { value: TeamScope; label: string; hint: string }[] = [
  { value: 'self', label: 'Managers only', hint: 'Just the people you tick — nobody below them.' },
  {
    value: 'direct',
    label: '+ Direct reportees',
    hint: 'The manager and the people who report straight to them — one level down.',
  },
  {
    value: 'all',
    label: '+ Entire team',
    hint: 'The manager and everyone beneath them, all the way down the hierarchy.',
  },
];

/** One past send, as returned by GET /announcements/history. */
export interface AnnouncementBatch {
  batchId: string;
  title: string;
  body: string;
  imageBlobPath: string | null;
  accentColor: string | null;
  decoration: string | null;
  createdBy: string | null;
  /** The sender as people read them: "Asha Menon (100231)", or the code alone. */
  sentBy: string | null;
  createdAt: string;
  expiresAt: string;
  countdownTo: string | null;
  recipients: number;
  dismissed: number;
  active: boolean;
  revokedAt: string | null;
  /** Absent on rows from before tasks existed — treat as 'message'. */
  kind?: 'message' | 'task';
  taskType?: string | null;
  /** Task batches only: recipients who have done the task. */
  completedCount?: number;
  /** false: nothing marks this send done (a message, or Read this with no button words), so it counts people. */
  tracksDone: boolean;
  /** "Reach a score" batches only: the score's current name and the month judged. */
  scoreTarget?: { metricName: string; month: string };
  /** Sends with a Complete by date only. */
  completeBy?: string;
  afterDue?: AfterDue;
  /** Of a send past its date: how many have not done it. */
  overdue?: number;
  fillForm?: { formName: string };
  reachLevel?: StoredLevel;
  /** Give consent only: how many answered which way. */
  answers?: { agreed: number; declined: number };
}

/** One row in either picker. `teamSize` is only set for managers. */
export interface PickOption {
  code: string;
  name: string;
  teamSize?: number;
}

/** What POST /announcements/recipients/parse gives back for an uploaded sheet. */
export interface RecipientParseResult {
  /** Roster-verified codes, in the roster's own casing. */
  codes: string[];
  /** code → display name, for codes the picker doesn't already know. */
  names?: Record<string, string>;
  matched: number;
  duplicates: number;
  invalid: number;
  scanned: number;
  hasHeader: boolean;
  column: string | null;
  errors: { row: number; employee_code: string; error: string }[];
  errorsTruncated?: boolean;
  /** Filled in client-side: how many of `codes` weren't already ticked. */
  added?: number;
}

/**
 * Accent swatches, ordered around the wheel so scanning them feels like a palette
 * rather than a list. All are mid-to-dark tones, because the accent doubles as text
 * colour on a near-white card — anything paler is still reachable through the custom
 * picker, which gets luminance-corrected.
 */
export const ACCENT_PRESETS: { label: string; value: string }[] = [
  { label: 'Slate', value: '#334155' },
  { label: 'Blue', value: '#2563EB' },
  { label: 'Sky', value: '#0284C7' },
  { label: 'Teal', value: '#0D9488' },
  { label: 'Green', value: '#059669' },
  { label: 'Lime', value: '#4D7C0F' },
  { label: 'Amber', value: '#D97706' },
  { label: 'Orange', value: '#EA580C' },
  { label: 'Red', value: '#DC2626' },
  { label: 'Rose', value: '#E11D48' },
  { label: 'Pink', value: '#DB2777' },
  { label: 'Fuchsia', value: '#C026D3' },
  { label: 'Purple', value: '#7C3AED' },
  { label: 'Indigo', value: '#4F46E5' },
  { label: 'Brown', value: '#78350F' },
  { label: 'Charcoal', value: '#1F2937' },
];

/**
 * Animated particles on the mobile card. Mirrors DECORATION_TYPES in
 * apps/api/src/utils/decorations.ts and apps/mobile/src/components/CardDecoration.tsx.
 */
export type DecorationType =
  | 'none'
  | 'balloons'
  | 'confetti'
  | 'lamps'
  | 'snowflakes'
  | 'rakhi'
  | 'flowers'
  | 'colors'
  | 'moon'
  | 'sparkles';

export const DECORATIONS: { value: DecorationType; label: string; emoji: string; hint?: string }[] = [
  { value: 'none', label: 'None', emoji: '—' },
  { value: 'balloons', label: 'Balloons', emoji: '🎈' },
  { value: 'confetti', label: 'Confetti', emoji: '🎊' },
  { value: 'lamps', label: 'Lamps', emoji: '🪔', hint: 'Diwali' },
  { value: 'snowflakes', label: 'Snowflakes', emoji: '❄️', hint: 'Christmas' },
  { value: 'rakhi', label: 'Rakhi', emoji: '🎀', hint: 'Raksha Bandhan' },
  { value: 'flowers', label: 'Flowers', emoji: '🌸', hint: 'Onam, Pongal' },
  { value: 'colors', label: 'Colours', emoji: '🎨', hint: 'Holi' },
  { value: 'moon', label: 'Moon & stars', emoji: '🌙', hint: 'Eid' },
  { value: 'sparkles', label: 'Sparkles', emoji: '✨' },
];

/** How long the card stays pinned to the home screen. */
export const DURATIONS = [
  { days: 1, label: '1 day' },
  { days: 3, label: '3 days' },
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
  { days: 30, label: '30 days' },
];

/**
 * The CEILING for a task card with no Complete by date. It normally goes much sooner, the
 * moment the employee does the task. The API caps it at 90.
 */
export const DEFAULT_TASK_DAYS = 90;

/**
 * Default decoration for a festival name. Mirrors inferDecoration() on the API
 * so the picker pre-selects what the server would have chosen anyway.
 */
export const inferDecoration = (name: string): DecorationType => {
  const lower = name.toLowerCase();
  if (lower.includes('diwali') || lower.includes('deepavali') || lower.includes('karva'))
    return 'lamps';
  if (lower.includes('christmas') || lower.includes('new year')) return 'snowflakes';
  if (lower.includes('raksha') || lower.includes('rakhi')) return 'rakhi';
  if (lower.includes('holi') || lower.includes('rang')) return 'colors';
  if (
    lower.includes('eid') ||
    lower.includes('ramadan') ||
    lower.includes('ramzan') ||
    lower.includes('bakrid') ||
    lower.includes('bakra') ||
    lower.includes('muharram') ||
    lower.includes('milad')
  )
    return 'moon';
  if (lower.includes('onam') || lower.includes('pongal') || lower.includes('ugadi'))
    return 'flowers';
  return 'sparkles';
};
